import { Injectable } from '@angular/core';
import { ApiService } from '../../services/api/api.service';
import { HelperService } from '../../services/utils/helper.service';
import { CONFIG } from '../../dataModels/config';
import {
  OPERATION,
  SORT_TYPE,
  TABLE_SORT_ORDER,
  CONSTANTS,
  PHONE_TYPE,
  COMPONENTS,
  TABLE_CELL_WIDTH,
  ORDER_WORKFLOW,
  VFC_MENU,
  VFC_MODES,
  STATE_FUND_ENROLL_COLUMN_VALUES,
} from '../../dataModels/constants';
import { ResourceManagerService } from '../../services/resourceManager/resource-manager.service';
import { LOV_KEYS } from '../../dataModels/lovs';
import { OrderHeader } from 'src/app/dataModels/Response/order-header';
import { CompAccessService } from '../../services/compAccess/comp-access.service';
import { lastValueFrom } from 'rxjs';
/**
 * Vaccine Ordering Service
 */
@Injectable({
  providedIn: 'root',
})
export class VaccineOrderingService {
  /**
   * index for order status
   */
  index = 0;

  /**
   * consider reenrollment period from 2017
   */
  START_RE_ENROLL_PERIOD = 2017;

  /**
   * constructor to inject dependency
   * @param apiService instance of apiService
   * @param helperService instance of helperService
   * @param resourceManagerService instance of resourceManagerService
   * @param compAccessService instance of component access service
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private resourceManagerService: ResourceManagerService,
    private compAccessService: CompAccessService
  ) { }

  /**
   * get facility order header
   * @param facilityId facility id of the user
   */
  async getFacilityOrderHeader(facilityId) {
    const request = {
      objects: [
        {
          name: OPERATION.VACCINE_ORDERING.GET_FACILITY_ORDER_HEADER,
          params: {
            facilityId,
          },
        },
      ],
    };

    let facilityOrder: OrderHeader;

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0] && result.objects[0].json && result.objects[0].json.length > 0) {
      facilityOrder = result.objects[0].json[0];
    }

    return facilityOrder;
  }

  /**
   * get order headers for table
   */
  getOrderHeaders() {
    return [
      {
        text: '',
        field: 'select',
        cellTemplate: 'CHECKBOX',
        noSort: true,
        cssClass: TABLE_CELL_WIDTH.ACTION,
        isFrozen: true,
      },
      {
        text: 'Order No.',
        field: 'orderItemSeq',
        cellTemplate: 'LINK',
        sort: true,
        sortType: SORT_TYPE.NUMBER,
        sortOrder: TABLE_SORT_ORDER.ASC,
        cssClass: TABLE_CELL_WIDTH.SMALL,
        isFrozen: true,
      },
      {
        text: 'Vaccine ID',
        field: 'vaccineId',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Brand',
        field: 'brandName',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'NDC Code',
        field: 'ndcCode',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Funding',
        field: 'cdcFundingType',
        sortType: SORT_TYPE.NUMBER,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Ord Int',
        field: 'orderingInt',
        sortType: SORT_TYPE.NUMBER,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Mfg',
        field: 'manufacturer',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Device',
        field: 'deviceDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Qty',
        field: 'qtyOrdered',
        sortType: SORT_TYPE.NUMBER,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Qty Sugg',
        field: 'qtySuggested',
        sortType: SORT_TYPE.NUMBER,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Qty Received',
        field: 'qtyReceived',
        sortType: SORT_TYPE.NUMBER,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Inventory',
        field: 'qtyAvailInProvInv',
        sortType: SORT_TYPE.NUMBER,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Status',
        field: 'orderDetailStatus',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Item Cost',
        field: 'itemCostDescr',
        sortType: SORT_TYPE.NUMBER,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      } 
    ];
  }

  /**
   * get facility order details
   * @param isUserAdmin indicate if logged in user is admin
   * @param facilityId facility id for which the order details has to be retrieved
   * @param startDate start date to retrieve order details for a facility
   * @param endDate end date to retrieve order details for a facility
   * @param orderWorkflow indicate if the workflow is VFC or COVID orders
   */
  async getFacilityOrderDetails(isUserAdmin, facilityId, orderWorkflow = 'VFC', startDate?, endDate?) {
    const params = {} as any;
    params.facilityId = facilityId;
    if (startDate) {
      params.startDate = startDate;
    }
    if (endDate) {
      params.endDate = endDate;
    }
    params.orderWorkflow = orderWorkflow;
    const request = {
      objects: [
        {
          name: OPERATION.VACCINE_ORDERING.GET_FACILITY_ORDER_DETAILS,
          params,
        },
      ],
    };

    let orderStatusList = [];

    const formattedOrderStatusList = [];

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      if (orderWorkflow === 'VFC') {
        orderStatusList = result.objects[0].json.filter((x) => x.vfcVaccineName.indexOf('COVIDDD') === -1);
      } else {
        orderStatusList = result.objects[0].json;
      }
      orderStatusList = this.sortOrderStatusList(orderStatusList);

      let i = 0;
      while (i < orderStatusList.length) {
        this.index = 0;
        i = this.createOrderListDS(orderStatusList[i].provOrderSeq, orderStatusList, formattedOrderStatusList, i);
      }

      this.modifyOrderList(formattedOrderStatusList, isUserAdmin);
    }

    return formattedOrderStatusList;
  }

  /**
   * sort the orders by date
   * @param orderStatusList list of orders
   */
  sortOrderStatusList(orderStatusList) {
    return orderStatusList.sort((a, b) => {
      const dt1 = new Date(a.orderDate).setHours(0, 0, 0, 0);
      const dt2 = new Date(b.orderDate).setHours(0, 0, 0, 0);
      return dt1 > dt2 ? -1 : dt1 < dt2 ? 1 : 0;
    });
  }

  /**
   * Convert flat order status list to heirarchy order status list
   * @param provOrderSeq order seq
   * @param sourceOrderStatusList source order status list
   * @param destOrderStatusList destination order status list
   * @param index index
   */
  private createOrderListDS(provOrderSeq, sourceOrderStatusList, destOrderStatusList, index) {
    let isPushedToDs = false;
    let dataSourceIndex;

    destOrderStatusList.forEach((ds, i) => {
      if (ds.provOrderSeq.trim() === provOrderSeq.trim()) {
        isPushedToDs = true;
        dataSourceIndex = i;
      }
    });

    sourceOrderStatusList.forEach((sourceOrderStatus, i) => {
      if (sourceOrderStatus.provOrderSeq.trim() === provOrderSeq.trim()) {
        index++;
        if (!isPushedToDs) {
          destOrderStatusList.push({
            SerialNum: this.index,
            orderDate: sourceOrderStatus.orderDate,
            status: '',
            totalOrderCost: sourceOrderStatus.totalOrderCost,
            orderedBy: sourceOrderStatus.orderedBy,
            provOrderSeq: sourceOrderStatus.provOrderSeq,
            rowData: [],
          });
          dataSourceIndex = destOrderStatusList.length - 1;
          this.index++;
          isPushedToDs = true;
        }
        sourceOrderStatus.manufacturer = this.resourceManagerService.getLovColumnProperties(
          LOV_KEYS.MANUFACTURER,
          sourceOrderStatus.mfgSeq,
          'descr'
        );
        sourceOrderStatus.deviceDescr = this.resourceManagerService.getLovColumnProperties(
          LOV_KEYS.INV_DEVICE,
          sourceOrderStatus.device,
          'descr'
        );
        sourceOrderStatus.brandName = this.resourceManagerService.getLovColumnProperties(
          LOV_KEYS.VACCINE_BRAND,
          sourceOrderStatus.brandSeq,
          'descr'
        );
        sourceOrderStatus.orderDetailStatus = this.resourceManagerService.getLovColumnProperties(
          LOV_KEYS.VFC_ORDER_ITEM_DETAIL_STATUS,
          sourceOrderStatus.status,
          'descr'
        );
        sourceOrderStatus.comments = this.helperService.decodeJsonString(sourceOrderStatus.comments);
        destOrderStatusList[dataSourceIndex].rowData.push(sourceOrderStatus);
      }
    });
    return index;
  }

  /**
   * modify order status list
   * @param orderList list of orders
   * @param isUserAdmin indicate if logged in user is of admin profile
   */
  private modifyOrderList(orderList, isUserAdmin) {
    this.setOrderStatus(orderList);
    for (const order of orderList) {
      for (const row of order.rowData) {
        row.canSelect = row.status === '2' && isUserAdmin;
        row.itemCostDescr = `$ ${row.itemCost}`;
      }
    }
  }

  /**
   * set order status
   * @param orderList list of orders
   */

  private setOrderStatus(orderList) {
    for (const order of orderList) {
      let orderStatus = '';
      const statusArr = this.count(order.rowData, (item) => {
        return item.status;
      });
      if (!this.helperService.IsNullOrEmpty(statusArr['2'])) {
        orderStatus = 'PENDING REVIEW';
      }
      // TRANSMITTED and NOT IN REVIEW
      else if (!this.helperService.IsNullOrEmpty(statusArr['5']) && this.helperService.IsNullOrEmpty(statusArr['2'])) {
        orderStatus = 'TRANSMITTED';
      }
      // SHIPPED AND NOT IN TRANSMITTED
      else if (!this.helperService.IsNullOrEmpty(statusArr['9']) && this.helperService.IsNullOrEmpty(statusArr['5'])) {
        orderStatus = 'SHIPPED';
      } else if (!this.helperService.IsNullOrEmpty(statusArr['6'])) {
        const rcvdStatus = statusArr['6'];
        let cancelledStatus = 0;
        if (!this.helperService.IsNullOrEmpty(statusArr['8'])) {
          cancelledStatus = statusArr['8'];
        }
        if (rcvdStatus + cancelledStatus === order.rowData.length) {
          orderStatus = 'CLOSED';
        }
      }
      // CANCELLED
      else if (!this.helperService.IsNullOrEmpty(statusArr['8']) && statusArr['8'] === order.rowData.length) {
        orderStatus = 'CANCELLED';
      } else {
        orderStatus = 'OPEN';
      }
      order.status = orderStatus;
    }
  }

  /**
   * gets the found elements and their occurences in array
   * @param ary array items
   * @param classifier what to find
   */
  private count(ary, classifier) {
    return ary.reduce((counter, item) => {
      const p = (classifier || String)(item);
      counter[p] = counter.hasOwnProperty(p) ? counter[p] + 1 : 1;
      return counter;
    }, {});
  }

  /**
   * check if user is admin
   * @param user logged in user
   */
  isUserAdmin(user) {
    return user?.profileType.toUpperCase() === 'VFC ADMIN';
  }

  /**
   * verify authorizing username and pin
   * @param userName userName
   * @param pin pin
   */
  async authorizeVaccineOrder(userName, pin) {
    const request = {
      objects: [
        {
          name: OPERATION.VACCINE_ORDERING.AUTHORIZE_VACCINE_ORDER,
          params: {
            userName,
            pin,
          },
        },
      ],
    };

    const authorizedUser = {
      isUserAuthorized: '',
      userIdSeq: '',
    };

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json.authorized) {
      authorizedUser.isUserAuthorized = result.objects[0].json.authorized;
      authorizedUser.userIdSeq = result.objects[0].json.authorizedUserIdSeq;
    }

    return authorizedUser;
  }

  /**
   * Manage Vaccine Order
   * @param orderData order data being edited
   * @param formValues order form values
   * @param user user performing the action
   * @param isUserAdmin indicate if user is admin
   * @param authByIdSeq authorization id seq
   */
  async manageVaccineOrder(orderData, formValues, user, isUserAdmin, authByIdSeq?) {
    const request = {
      action: 'UPDATE_VACCINE_ORDER',
      orderItemSeq: orderData.orderItemSeq,
      newQtyOrdered: formValues.qtyOrdered,
      newStatus: formValues.status || '',
      authByIdSeq,
      comments: this.helperService.appendUserAndDateForComments(formValues.comments, user.userName),
      adminFlag: isUserAdmin ? 'Y' : 'N',
    };

    return await lastValueFrom(this.apiService.post(CONFIG.MANAGE_VACCINE_ORDER, request));
  }

  /**
   * cancel vaccine order
   * @param orderData order data being cancelled
   * @param user user performing the action
   * @param formValues order form values
   */
  async cancelVaccineOrder(orderData, user, formValues) {
    const request = {
      action: 'CANCEL_VACCINE_ORDER',
      orderItemSeq: orderData.orderItemSeq,
      comments: this.helperService.appendUserAndDateForComments(formValues.comments, user.userName),
    };

    return await lastValueFrom(this.apiService.post(CONFIG.MANAGE_VACCINE_ORDER, request));
  }

  /**
   * open vaccine order
   * @param vaccineOrderList list of orders having orderItemSeq
   */
  async openVaccineOrder(vaccineOrderList) {
    const request = {
      action: 'OPEN_VACCINE_ORDER',
      vaccineOrderList,
    };
    return await lastValueFrom(this.apiService.post(CONFIG.MANAGE_VACCINE_ORDER, request));
  }

  /**
   * get facility new order eligiblity
   * @param facilityId facility id
   * @param vfcPin vfcPin
   * @param orderWorkflow indicate if the workflow is VFC or COVID orders
   */
  async getFacilityNewOrderEligibility(facilityId, vfcPin, orderWorkflow = ORDER_WORKFLOW.VFC) {
    const request = {
      objects: [
        {
          name: OPERATION.VACCINE_ORDERING.GET_FACILITY_NEW_ORDER_ELIGIBLITY,
          params: {
            facilityId,
            vfcPin,
            orderWorkflow,
          },
        },
      ],
    };

    const newOrderEligibility = {
      eligibility: '',
      reasonDenied: '',
    };

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      newOrderEligibility.eligibility = result.objects[0].json.facilityNewOrderEligiblity;
      newOrderEligibility.reasonDenied = result.objects[0].json.reasonDenied;
    }

    return newOrderEligibility;
  }

  /**
   * get emergency order eligiblity
   * @param facilityId facility id
   * @param daysBetweenOrders days between the orders
   */
  async getEmergencyOrderEligibility(facilityId, daysBetweenOrders) {
    const request = {
      objects: [
        {
          name: OPERATION.VACCINE_ORDERING.GET_FACILITY_EMERGENCY_ORDER_ELIGIBLITY,
          params: {
            facilityId,
            daysBetweenOrders,
          },
        },
      ],
    };

    let emergencyOrderEligiblity = '';

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      emergencyOrderEligiblity = result.objects[0].json.facilityEmergencyOrderEligiblity;
    }

    return emergencyOrderEligiblity;
  }

  /**
   * get vaccine order restriction
   */
  async getVaccineOrderRestriction() {
    const request = {
      objects: [
        {
          name: OPERATION.VACCINE_ORDERING.GET_VFC_ORDER_RESTRICTION,
        },
      ],
    };

    let vaccineOrderRestriction = {
      daysBeforeLotExp: '',
      daysBetweenOrders: '',
      daysOrderOnHoldInReview: '',
      wastePercReviewLevel: '',
      covidDaysBetweenOrders: '',
      stateFundDaysBetweenOrders: ''
    };

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json && result.objects[0].json.length > 0) {
      vaccineOrderRestriction = result.objects[0].json[0];
    }
    return vaccineOrderRestriction;
  }

  /**
   * headers for temperature log
   */
  getTemperatureLogHeaders() {
    return [
      {
        text: 'Unit Name',
        field: 'unitName',
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
      {
        text: 'Unit Type',
        field: 'unitTypeDescr',
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
      {
        text: 'Log Period',
        field: 'logPeriod',
        cellTemplate: 'LINK',
        showOverlay: true,
        overLayTemplateId: 'tempLogDetails',
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
      {
        text: 'Date Submitted',
        field: 'submitionDate',
        sortType: SORT_TYPE.DATE,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
    ];
  }

  /**
   * get eligible vaccines for the facility
   * @param facilityId facility id for which vaccines has to be retrieved
   * @param isPlaceFluOrder indicate if flu order is being placed, so use this to filter FLU and FLU MIST vaccines
   * @param orderWorkflow indicate if the workflow is VFC or COVID orders
   */
  async getFacilityEligibleVaccine(facilityId, isPlaceFluOrder, orderWorkflow = ORDER_WORKFLOW.VFC) {
    const request = {
      objects: [
        {
          name: OPERATION.VACCINE_ORDERING.GET_FACILITY_ELIGIBLE_VACCINE,
          params: {
            facilityId,
            orderWorkflow,
          },
        },
      ],
    };

    let vaccineList = [];

    const formattedVaccineList = [];

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json && result.objects[0].json.length > 0) {
      vaccineList = this.sortVaccineList(result.objects[0].json);
      if (isPlaceFluOrder) {
        vaccineList = this.filterFluVaccines(vaccineList);
      }
      this.modifyVaccineList(vaccineList);
      this.index = 0;
      let i = 0;
      while (i < vaccineList.length) {
        i = this.createPlaceOrderDS(vaccineList[i].vaccineId, vaccineList, formattedVaccineList, i, vaccineList[i].vfcVaccineName);
      }
    }

    return formattedVaccineList;
  }

  /**
   * sort vaccine list by name
   * @param vaccineList list of vaccines
   */
  sortVaccineList(vaccineList: any[]) {
    return vaccineList.sort((a, b) => {
      const d1 = a.vfcVaccineName;
      const d2 = b.vfcVaccineName;
      return d1 < d2 ? -1 : d1 > d2 ? 1 : 0;
    });
  }

  /**
   * filter only FLU and FLU mist vaccines from the list
   * @param vaccineList list of vaccines
   */
  filterFluVaccines(vaccineList: any[]) {
    return vaccineList.filter((x) => x.vaccineId.toUpperCase().trim() === 'FLU' || x.vaccineId.toUpperCase().trim() === 'FLU-MIST' || x.vaccineId.toUpperCase().trim() === 'COVID-19');
  }

  /**
   * modify vaccines list
   * @param vaccineList list of vaccines
   */
  modifyVaccineList(vaccineList: any[]) {
    for (const vaccine of vaccineList) {
      vaccine.deviceDescr = this.resourceManagerService.getLovColumnProperties(LOV_KEYS.INV_DEVICE, vaccine.device, 'descr');
      vaccine.description = `${vaccine.devicesPerPack} pack - ${vaccine.dosesPerDevice} dose ${vaccine.deviceDescr}`;
      vaccine.brandName = this.resourceManagerService.getLovColumnProperties(LOV_KEYS.VACCINE_BRAND, vaccine.brandSeq, 'descr');
      if (!this.helperService.IsNullOrEmpty(vaccine.invWasted)) {
        vaccine.invWastedNumber = +vaccine.invWasted;
      } else {
        vaccine.invWastedNumber = 0;
        vaccine.invWasted = 0;
      }
      if (!this.helperService.IsNullOrEmpty(vaccine.projection)) {
        vaccine.projectionNumber = +vaccine.projection;
      }
      if (!this.helperService.IsNullOrEmpty(vaccine.invBalance)) {
        vaccine.invBalanceNumber = +vaccine.invBalance;
      } else {
        vaccine.invBalanceNumber = 0;
        vaccine.invBalance = 0;
      }
      vaccine.ordDoses = '';
    }
  }

  /**
   * create place order data structure
   * @param vaccineId vaccine Id
   * @param sourceVaccineList source vaccine list
   * @param destVaccineList modified vaccine list for ui rendering
   * @param index index
   * @param vfcVaccineName name of vaccine
   */
  createPlaceOrderDS(vaccineId, sourceVaccineList, destVaccineList, index, vfcVaccineName) {
    let isPushedToDs = false;
    sourceVaccineList.forEach((vaccine, i) => {
      if (vaccine.vfcVaccineName === vfcVaccineName) {
        index++;
        if (!isPushedToDs) {
          destVaccineList.push({
            SerialNum: this.index,
            vaccineId,
            vfcVaccineName,
            rowData: [],
          });
          this.index++;
          isPushedToDs = true;
        }
        destVaccineList[destVaccineList.length - 1].rowData.push(sourceVaccineList[i]);
      }
    });
    return index;
  }

  /**
   * Place vaccine order
   * @param user logged in user placing the order
   * @param facilityOrder facility order details
   * @param isUserAdmin indicat if user placing the order is admin user
   * @param shippingInst shipping instruction
   * @param comments comments entered by user
   * @param vaccineList list of vaccines orders to be placed
   * @param authByIdSeq authorization user if order is authorized
   * @param orderWorkflow  ndicate if workflow is for VFC Vaccine or COVID Vaccine
   */
  async placeVaccineOrder(
    user,
    facilityOrder,
    isUserAdmin,
    shippingInst,
    comments,
    vaccineList,
    authByIdSeq?,
    orderWorkflow?
  ) {
    const request = {
      action: 'PLACE_VACCINE_ORDER',
      facilityId: user.currentFacilityId,
      vfcPin: user.currentVfcPin,
      invVaccineSource: orderWorkflow === ORDER_WORKFLOW.VFC ? facilityOrder.invVaccineSource : orderWorkflow === ORDER_WORKFLOW.COVID ? facilityOrder.covidInvVaccineSource : facilityOrder.vaccFundInvVaccineSource,
      facilityCategory: facilityOrder.provCategory,
      adminUserFlag: isUserAdmin ? 'Y' : 'N',
      orderedByUser: user.userName,
      shippingInst,
      comments: this.helperService.encodeJsonString(comments),
      authByIdSeq,
      facilityVaccineOrderList: this.formFacilityVaccineOrderList(vaccineList, isUserAdmin, orderWorkflow),
    };

    return await lastValueFrom(this.apiService.post(CONFIG.MANAGE_VACCINE_ORDER, request));
  }

  /**
   * form facility vaccine order list
   * @param orderList list of orders
   * @param isUserAdmin indicate if user is admin
   * @param orderWorkflow  ndicate if workflow is for VFC Vaccine or COVID Vaccine
   */
  formFacilityVaccineOrderList(vaccineList, isUserAdmin, orderWorkflow) {
    const facilityVaccineOrderList = [];
    for (const vaccine of vaccineList) {
      for (const order of vaccine.ordersList) {
        facilityVaccineOrderList.push({
          vfcItemSeq: order.vfcItemSeq,
          doseQty: order.doses,
          inventoryBalance: this.helperService.IsNullOrEmpty(order.invBalance) ? '0' : order.invBalance,
          qtySuggested: vaccine.projection,
          orderStatus:
            orderWorkflow === ORDER_WORKFLOW.VFC
              ? this.getOrderStatus(order.doses, vaccine.projection, order.ordRestrictionQty, isUserAdmin)
              : '2',
        });
      }
    }

    return facilityVaccineOrderList;
  }

  /**
   * get the order status
   * if dosesEntered is more than the restricted quantity, order status is 2 (REVIEW ORDER)
   * else if dosesEntered is more thatn the suggested quantity,
   * and user placing the order is not admin user, order status is 2 (REVIEW ORDER)
   * else orderStatus will be 1 (OPEN ORDER)
   * @param doses doses being ordered
   * @param projection projected quantity
   * @param orderRestrictionQty order restriction quantity
   * @param isUserAdmin indicate if user is admin
   */
  getOrderStatus(doses, projection, orderRestrictionQty, isUserAdmin) {
    let orderStatus;
    const dosesEntered = +doses;
    const qtySuggested = +projection;
    const restrictedQty = +orderRestrictionQty;
    if (dosesEntered > restrictedQty && !this.helperService.IsNullOrEmpty(orderRestrictionQty)) {
      orderStatus = '2'; // REVIEW ORDER
    } else if (dosesEntered > qtySuggested && !isUserAdmin) {
      orderStatus = '2'; // REVIEW ORDER
    } else {
      orderStatus = '1'; // OPEN ORDER
    }
    return orderStatus;
  }

  /**
   * get address, phone number and email address of facility
   * @param facilityId facilityId for which address and phone has to be retrieved
   */
  async getAddressPhoneAndEmailOfFacility(facilityId) {
    const request = {
      objects: [
        {
          name: OPERATION.GLOBAL.GET_ADDRESS,
          params: {
            facilityId,
          },
        },
        {
          name: OPERATION.GLOBAL.GET_PHONE,
          params: {
            facilityId,
          },
        },
        {
          name: OPERATION.GLOBAL.GET_EMAIL,
          params: {
            facilityId,
          },
        },
      ],
    };

    const facility = {
      address: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        stateDescr: '',
        county: '',
        municipality: '',
        zip: '',
      },
      phoneNumber: '',
      phoneExtn: '',
      faxNumber: '',
      emailAddress: '',
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_GLOBAL_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects.length > 0) {
      for (const resObj of result.objects) {
        if (
          resObj.name === OPERATION.GLOBAL.GET_ADDRESS &&
          (resObj.status === CONSTANTS.SUCCESS || resObj.status === CONSTANTS.TRN_WARNING)
        ) {
          const address = resObj.json[0];
          facility.address.addressLine1 = address.addressLine1;
          facility.address.addressLine2 = address.addressLine2;
          facility.address.city = address.city;
          facility.address.state = address.state;
          facility.address.stateDescr = this.resourceManagerService.getLovColumnProperties(LOV_KEYS.STATE, address.state, 'descr');
          facility.address.county = address.county;
          facility.address.municipality = address.municipality;
          facility.address.zip = address.zip;
        } else if (
          resObj.name === OPERATION.GLOBAL.GET_PHONE &&
          (resObj.status === CONSTANTS.SUCCESS || resObj.status === CONSTANTS.TRN_WARNING)
        ) {
          for (const phone of resObj.json) {
            if (phone.phoneType === PHONE_TYPE.WORK) {
              facility.phoneNumber = this.helperService.formatPhone(phone.phoneNumber);
              facility.phoneExtn = phone.phoneExtn;
            } else if (phone.phoneType === PHONE_TYPE.FAX) {
              facility.faxNumber = this.helperService.formatPhone(phone.phoneNumber);
            }
          }
        } else if (
          resObj.name === OPERATION.GLOBAL.GET_EMAIL &&
          (resObj.status === CONSTANTS.SUCCESS || resObj.status === CONSTANTS.TRN_WARNING)
        ) {
          if (resObj?.json) {
            facility.emailAddress = resObj?.json[0]?.emailAddress;
          }
        }
      }
    }

    return facility;
  }

  /**
   * check user access for manage order
   */
  checkManageOrderCompAccess() {
    return this.compAccessService.checkAccess([COMPONENTS.VACCINE_ORDERING.MANAGE_ORDER]);
  }

  /**
   * Get VFC, VFC Inprogress for Facility
   * @param facilityId facilityId
   * @param vfcPin vfcPin of the user performing ReEnrollment
   * @param enrollmentPeriod period of the enrollment
   */
  async getVFCForFacility(facilityId, vfcPin, enrollmentPeriod, isReenrollment ,vfcIdSeq) {
    let request = {} as any;

    if (isReenrollment) {
      request = {
        objects: [
          {
            name: OPERATION.VFC.GET_VFC,
            params: {
              facilityId,
              enrollmentType: "2",
            },
          },
          {
            name: OPERATION.VFC.GET_VFC_INPROGRESS,
            params: {
              facilityId,
              vfcPin,
              enrollmentPeriod,
            },
          },
        ],
      };
    } else {
      if (vfcPin) {
        request = {
          objects: [

            {
              name: OPERATION.VFC.GET_VFC,
              params: {
                facilityId,
                 enrollmentType: "1",
                vfcPin,
                vfcIdSeq
              },
            },
            {
              name: OPERATION.VFC.GET_VFC_INPROGRESS,
              params: {
                facilityId,
                enrollmentType: "1",
                vfcPin,
                vfcIdSeq
              },
            },
          ],
        };

      } else {
        request = {
          objects: [

            {
              name: OPERATION.VFC.GET_VFC_INPROGRESS,
              params: {
                facilityId,
                enrollmentType: "1"
              },
            },
          ],
        };
      }


    }


    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_VFC_BUNDLE, request));

    const vfcList = {
      inProgressList: [],
      existingList: [],
      isError: false,
    };

    if (this.helperService.isValidGetResult(result)) {
      for (const vfcResult of result.objects) {
        if (vfcResult.name === OPERATION.VFC.GET_VFC_INPROGRESS) {
          vfcList.inProgressList = vfcResult.json;
        } else if (vfcResult.name === OPERATION.VFC.GET_VFC && vfcResult.json.vfc && vfcResult.json.vfc.json) {
          vfcList.existingList = vfcResult.json.vfc.json;
        }
      }
    } else {
      vfcList.isError = true;
    }

    return vfcList;
  }

  /**
   * headers for review orders table
   */
  getFacilityReviewOrderHeaders() {
    return [
      {
        text: 'Order Date',
        field: 'orderDate',
        sortType: SORT_TYPE.DATE,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'PIN',
        field: 'vfcId',
        cellTemplate: 'LINK',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Provider',
        field: 'facilityName',
        cssClass: TABLE_CELL_WIDTH.LARGE,
      },
      {
        text: 'Total Items in Review Status',
        field: 'reviewOrderCount',
        sortType: SORT_TYPE.NUMBER,
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
    ];
  }

  /**
   * get count of orders in review
   * @param orderWorkflow indicate if the workflow is for COVID Vaccine or VFC Vaccine
   */
  async getOrdersReviewCount(orderWorkflow = ORDER_WORKFLOW.VFC) {
    const request = {
      objects: [
        {
          name: OPERATION.VACCINE_ORDERING.GET_ORDER_ITEM_REVIEW_COUNT,
          params: {
            orderWorkflow,
          },
        },
      ],
    };

    let orderInReviewCount = '';

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      orderInReviewCount = result.objects[0].json.orderItemReviewCount;
    }

    return orderInReviewCount;
  }

  /**
   * get orders for the facility
   * @param orderItemStatus status of the orders to be retrived
   * @param startDate start date from which to retrieve the orders
   * @param endDate end date till which the orders has to be retrieved
   * @param facilityTypeList list of covidFacilityTypes, the value will be blank for VFC Review Order
   * @param countyList list of countyList, the value will be blank for VFC Review Order
   * @param orderWorkflow indicate if the workflow is for COVID Vaccine or VFC Vaccine
   */
  async getFacilityOrders(orderItemStatus, startDate, endDate, facilityTypeList = '', countyList = '', orderWorkflow = ORDER_WORKFLOW.VFC) {
    const request = {
      objects: [
        {
          name: OPERATION.VACCINE_ORDERING.GET_FACILITY_ORDER,
          params: { orderItemStatus, startDate, endDate, facilityTypeList, countyList, orderWorkflow },
        },
      ],
    };

    const facilityOrders = {
      headers: this.getFacilityReviewOrderHeaders(),
      ordersList: [],
    };

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      facilityOrders.ordersList = result.objects[0].json;
      if (orderItemStatus === '1') {
        facilityOrders.headers[3].text = 'Total Items in Open Status';
        facilityOrders.headers[3].field = 'openOrderCount';
      }
    }

    return facilityOrders;
  }

  /**
   * headers for waste extract grid
   */
  getWasteExtractHeaders() {
    return [
      {
        text: 'Extract Start Date',
        field: 'extcStartDate',
        sortType: SORT_TYPE.DATE,
        sort: true,
        sortOrder: TABLE_SORT_ORDER.DESC,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Extract End Date',
        field: 'extcEndDate',
        sortType: SORT_TYPE.DATE,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Expired Extract Count',
        field: 'extcExpCount',
        cellTemplate: 'LINK',
        sortType: SORT_TYPE.NUMBER,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Provider Extract Count',
        field: 'extcProvCount',
        cellTemplate: 'LINK',
        sortType: SORT_TYPE.NUMBER,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Waste Extract Count',
        field: 'extcWasCount',
        cellTemplate: 'LINK',
        sortType: SORT_TYPE.NUMBER,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
    ];
  }

  /**
   * get waste extract
   */
  async getWasteExtract() {
    const request = {
      objects: [
        {
          name: OPERATION.VACCINE_ORDERING.GET_WASTE_EXTRACT_EVENT,
        },
      ],
    };

    let wasteExtractList = [];

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_CORE_INV_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      wasteExtractList = result.objects[0].json;
    }

    return wasteExtractList;
  }

  /**
   * execute waste extract
   */
  async executeWasteExtract() {
    return await lastValueFrom(this.apiService.post(CONFIG.CORE_EXECUTE_WASTE_EXTRACT, {}));
  }

  /**
   * download extract data
   * @param extractSeq extract seq
   * @param extractName extract name
   */
  async downloadWasteExtract(extractSeq, extractName) {
    const request = {
      extractSeq,
      extractName,
    };

    return await lastValueFrom(this.apiService.msPost(CONFIG.DOWNLOAD_EXTRACT, request));
  }

  /**
   * get reenrollment headers
   */

  getReenrollmentHeaders() {
    return [
      {
        text: '',
        field: 'select',
        cssClass: TABLE_CELL_WIDTH.ACTION,
        cellTemplate: 'CHECKBOX',
        noSort: true,
        isFrozen: true,
      },
      {
        text: 'Re-enrollment No',
        field: 'vfcEnrollFormSeq',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.NUMBER,
        cellTemplate: 'LINK',
        sort: true,
        sortOrder: TABLE_SORT_ORDER.DESC,
        isFrozen: true,
      },
      {
        text: 'Facility Name',
        field: 'facilityName',
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
      {
        text: 'VFC Pin',
        field: 'vfcPin',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Re-enrollment Type',
        field: 'enrollmentTypeDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Re-enrollment Period',
        field: 'enrollmentPeriodDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Submission Date',
        field: 'submissionDate',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.DATE,
      },
      {
        text: 'Status',
        field: 'enrollStatusDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Reviewer',
        field: 'reviewerName',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'VFC Cert Date',
        field: 'currentVFCCertificationDate',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.DATE,
      },
    ];


  }



  getEnrollmentHeaders() {
    return [
      // {
      //   text: '',
      //   field: 'select',
      //   cssClass: TABLE_CELL_WIDTH.ACTION,
      //   cellTemplate: 'CHECKBOX',
      //   noSort: true,
      //   isFrozen: true,
      // },
      {
        text: 'Enrollment No',
        field: 'vfcEnrollFormSeq',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.NUMBER,
        cellTemplate: 'LINK',
        sort: true,
        sortOrder: TABLE_SORT_ORDER.DESC,
        isFrozen: true,
      },
      {
        text: 'Facility Name',
        field: 'facilityName',
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
      
      {
        text: 'VFC Type',
        field: 'enrollmentTypeDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'VFC Facility Type',
        field: 'vfcFacilityTypeDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'County',
        field: 'countyDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Enrollment Status',
        field: 'enrollStatusDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Date  Submiiited',
        field: 'submissionDate',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.DATE,
      },
    
      {
        text: 'Reviewer',
        field: 'reviewerName',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'COVID-19 Pin',
        field: 'covidPin',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.DATE,
      },
      {
        text: 'VFC Pin',
        field: 'vfcPin',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'VFC Status',
        field: 'vfcStatus',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.DATE,
      },
      
    ];
  }



  async getReenrollmentForms(formValues, reviewUserList) {

    let vfcTypeList = [];
    if (formValues.reenrollmentType) {
      vfcTypeList = [formValues.reenrollmentType];
    }
    let reviewerList = [];
    console.log(formValues.reviewer);
    if (formValues.reviewer) {
      reviewerList = [formValues.reviewer];
    }

    let statusList = [];
    if (formValues.status) {
      statusList = [formValues.status];
    }

    const request = {
      objects: [
        {
          name: OPERATION.VFC.GET_VFC_INPROGRESS,
          params: {
            vfcEnrollFormSeq: formValues.reenrollmentNum || '',
            facilityId: formValues.facilityId,
            vfcPin: formValues.vfcPin || '',
            facilityName: formValues.facilityName || '',
            enrollmentType: "2",
            enrollmentPeriod: formValues.reenrollmentPeriod || '',
            submissionDateFrom: this.helperService.formatDate(formValues.startDate),
            submissionDateTo: this.helperService.formatDate(formValues.endDate),
            enrollmentFormStatus: statusList || [],
            reviewerIdSeq: reviewerList || [],
            vfcType: vfcTypeList || []
          },
        },
      ],
    };

    let reenrollmentList = [];

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_VFC_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      reenrollmentList = this.formatReenrollmentList(result.objects[0].json, reviewUserList);
    }


    return reenrollmentList;
  }

  /**
   * search for reenrollment forms
   * @param formValues seach reenrollment form values
   * @param reviewUserList list of users who will review the forms
   */
  async getEnrollmentForms(formValues, reviewUserList, facilityList, countyMuniList) {
    let statusList = [];
    if (formValues.status?.length) {
      statusList = formValues.status.map(status => status.columnValue);
    }
    let reviewerList = [];
    console.log(formValues.reviewer);
    if (formValues.reviewer?.length) {
      reviewerList = formValues.reviewer.map(rev => rev.idSeq);
    }
    const request = {
      objects: [
        {
          name: OPERATION.VFC.GET_VFC_INPROGRESS,
          params: {
            vfcEnrollFormSeq: formValues.vfcEnrollFormSeq || '',
            facilityId: formValues.facilityId,
            vfcPin: formValues.vfcPin || '',
            facilityName: formValues.facilityName || '',
            enrollmentType: "1",
            //  enrollmentPeriod: formValues.reenrollmentPeriod || '',
            submissionDateFrom: this.helperService.formatDate(formValues.startDate),
            submissionDateTo: this.helperService.formatDate(formValues.endDate),
            enrollmentFormStatus: statusList || [],
            reviewerIdSeq: reviewerList || [],
            vfcType: formValues.vfcType?.split(",") || []
          },
        },
      ],
    };

    let enrollmentList = [];

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_VFC_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      enrollmentList = this.formatEnrollmentList(result.objects[0].json, reviewUserList, facilityList, countyMuniList);
    }



    return enrollmentList;
  }


  /**
   * format the contents of the reenrollment list
   * @param reenrollmentList list of reenrollments
   * @param reviewUserList list of users who will review the forms
   */
  formatEnrollmentList(enrollmentList, reviewUserList, facilityList, countyMuniList) {
    const enrollList = [];
    for (const item of enrollmentList) {
      item.canSelect = true;
      item.enrollmentTypeDescr = this.resourceManagerService.getLovColumnProperties(LOV_KEYS.VFC_TYPE, item.vfcType, 'descr');
      item.enrollmentPeriodDescr = this.resourceManagerService.getLovColumnProperties(
        LOV_KEYS.VFC_RE_ENROLL_PERIOD,
        item.enrollmentPeriod,
        'descr'
      );
      var matchedFacilityTypeList = facilityList[1].filter(function(fac) {
        return fac.columnValue == item.vfcFacilityType;
    });
    if (matchedFacilityTypeList && matchedFacilityTypeList.length > 0) {
        item.vfcFacilityTypeDescr = matchedFacilityTypeList[0].descr;


 }

 var county = item.county.substring(0, 2) + '00';
 var matchedCounty = countyMuniList.filter(function(countyItem) {
     return countyItem.countyCode === county;
 });
 if (matchedCounty && matchedCounty.length > 0) {
     item.countyDescr = matchedCounty[0].countyName;
 }

      item.reviewerName = this.getUserName(reviewUserList, item.reviewerIdSeq);
      if (!this.helperService.IsNullOrEmpty(item.vfcCertDate)) {
        item.currentVFCCertificationDate = item.vfcCertDate;
      }
      if (
        item.enrollStatusDescr === 'IN REVIEW' ||
        item.enrollStatusDescr === 'SUBMITTED' ||
        item.enrollStatusDescr === 'APPROVED' ||
        item.enrollStatusDescr === 'ACTIVATED' ||
        item.enrollStatusDescr === 'REJECTED'
      ) {
        item.isReviewer = true;
      } else {
        item.isReviewer = false;
      }

      if (item.enrollStatusDescr !== 'IN PROGRESS') {
        item.canEdit = true;
        enrollList.push(item);
      }
    }

    return enrollList;
  }

  /**
   * format the contents of the reenrollment list
   * @param reenrollmentList list of reenrollments
   * @param reviewUserList list of users who will review the forms
   */
  formatReenrollmentList(reenrollmentList, reviewUserList) {
    const reEnrollList = [];
    for (const item of reenrollmentList) {
      item.canSelect = true;
      item.enrollmentTypeDescr = this.resourceManagerService.getLovColumnProperties(LOV_KEYS.VFC_TYPE, item.vfcType, 'descr');
      item.enrollmentPeriodDescr = this.resourceManagerService.getLovColumnProperties(
        LOV_KEYS.VFC_RE_ENROLL_PERIOD,
        item.enrollmentPeriod,
        'descr'
      );
      item.reviewerName = this.getUserName(reviewUserList, item.reviewerIdSeq);
      if (!this.helperService.IsNullOrEmpty(item.vfcCertDate)) {
        item.currentVFCCertificationDate = item.vfcCertDate;
      }
      if (
        item.enrollStatusDescr === 'IN REVIEW' ||
        item.enrollStatusDescr === 'SUBMITTED' ||
        item.enrollStatusDescr === 'APPROVED' ||
        item.enrollStatusDescr === 'ACTIVATED' ||
        item.enrollStatusDescr === 'REJECTED'
      ) {
        item.isReviewer = true;
      } else {
        item.isReviewer = false;
      }

      if (item.enrollStatusDescr !== 'IN PROGRESS' && this.doConsiderReEnrollmentPeriod(item)) {

        reEnrollList.push(item);
      }
    }

    return reEnrollList;
  }

  /**
   * get name of the user
   * @param userList list of users
   * @param idSeq idSeq
   */
  getUserName(userList, idSeq) {
    const matchedUserList = userList.filter((x) => x.idSeq === idSeq);
    return matchedUserList?.length > 0 ? matchedUserList[0].name : '';
  }

  /**
   * check if the reenrollment period is greater than 2017
   * @param reEnrollObj reenrollment item
   */
  doConsiderReEnrollmentPeriod(reEnrollObj) {
    const enrollPeriodArr = reEnrollObj.enrollmentPeriodDescr.split('-');
    return +enrollPeriodArr[0] >= this.START_RE_ENROLL_PERIOD;
  }

  /**
   * update order restriction
   * @param daysBetweenOrders days between orders
   * @param orderWorkflow indicate if the workflow is VFC or COVID orders
   */
  async updateOrderRestriction(daysBetweenOrders, orderWorkflow = ORDER_WORKFLOW.VFC) {
    const request = {
      daysBetweenOrders,
      orderWorkflow,
    };

    return await lastValueFrom(this.apiService.post(CONFIG.UPDATE_ORDER_RESTRICTION, request));
  }

  /**
   * check if user has access to manage vaccine restrictions
   */
  async checkManageVaccineRestrictionAccess() {
    return this.compAccessService.checkAccess([COMPONENTS.VACCINE_ORDERING.MANAGE_VACCINE_RESTRICTIONS]);
  }

  /**
   * headers for vaccine restiction table
   */
  getVaccineRestrictionHeaders() {
    return [
      {
        text: 'VFC Vaccine Name',
        field: 'vfcVaccineName.value.data',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Vaccine ID',
        field: 'vaccineId',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'NDC Code',
        field: 'ndcCode',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Funding',
        field: 'cdcFundingType',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Ord Int',
        field: 'orderingInt',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Brand',
        field: 'brandName',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Mfg',
        field: 'vacmanMfgCode',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Device',
        field: 'deviceDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
    ];
  }

  /**
   * get vaccine items
   * @param orderWorkflow indicate if the workflow is VFC or COVID orders
   */
  async getVaccineItems(orderWorkflow = ORDER_WORKFLOW.VFC) {
    const request = {
      objects: [
        {
          name: OPERATION.VACCINE_ORDERING.GET_VACCINE_ITEMS,
          params: {
            orderWorkflow,
          },
        },
      ],
    };

    let vaccineList = [];

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      vaccineList = result.objects[0].json;
      this.formatVaccineList(vaccineList);
    }

    return vaccineList;
  }

  /**
   * format vaccines, add descr for device, brand
   * @param vaccineList list of vaccines
   */
  formatVaccineList(vaccineList) {
    for (const vaccine of vaccineList) {
      vaccine.deviceDescr = this.resourceManagerService.getLovColumnProperties(LOV_KEYS.INV_DEVICE, vaccine.device, 'descr');
      vaccine.brandName = this.resourceManagerService.getLovColumnProperties(LOV_KEYS.VACCINE_BRAND, vaccine.brandSeq, 'descr');
      vaccine.packaging = vaccine.devicesPerPack + ' pack -' + vaccine.dosesPerDevice + ' dose ' + vaccine.deviceDescr;
      vaccine.cdcCost = `$${vaccine.cdcCost}`;
      vaccine.isDisabled = true;
      vaccine.isVaccRestriction = '';
      vaccine.ordRestrictionQty = 'N/A';
    }
  }

  /**
   * get facility vaccine restriction list
   */
  async getFacilityVaccineRestrictionList() {
    const request = {
      objects: [
        {
          name: OPERATION.VACCINE_ORDERING.GET_FACILITY_VACCINE_RESTRICTION,
        },
      ],
    };

    let facilityVaccRestrictionList = [];

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      facilityVaccRestrictionList = result.objects[0].json;
    }

    return facilityVaccRestrictionList;
  }

  /**
   * manage vaccine restriction list
   * @param facilityCategory facility Category
   * @param vaccineRestrictionList vaccine restriction list
   */
  async manageVaccineRestriction(facilityCategory, vaccineRestrictionList, orderWorkflow = ORDER_WORKFLOW.VFC) {
    const request = {
      facilityCategory,
      vaccineRestrictionList,
      orderWorkflow
    };

    return await lastValueFrom(this.apiService.post(CONFIG.MANAGE_FACILITY_VACCINE_RESTRICTION, request));
  }

  /**
   * check if user has access to manage vaccine restrictions
   */
  async checkManageVaccineList() {
    return this.compAccessService.checkAccess([COMPONENTS.VACCINE_ORDERING.MANAGE_VACCINE_LIST]);
  }

  /**
   * vaccine list table headers
   */
  getVaccineListHeaders() {
    return [
      {
        text: 'Vaccine',
        field: 'vaccineId',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        cellTemplate: 'LINK',
        isFrozen: true,
      },
      {
        text: 'Brand',
        field: 'brandName',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        isFrozen: true,
      },
      {
        text: 'NDC Code',
        field: 'ndcCode',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        isFrozen: true,
      },
      {
        text: 'Funding',
        field: 'cdcFundingType',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Ord Int',
        field: 'orderingInt',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Packaging',
        field: 'packaging',
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
      {
        text: 'Cost',
        field: 'cdcCost',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.NUMBER,
      },
      {
        text: 'End Date',
        field: 'contractEndDate',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.DATE,
      },
      {
        text: 'Mfg',
        field: 'vacmanMfgCode',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      }
    ];
  }

  /**
   * Manage Vaccine
   * @param action action being performed UPDATE_CONTRACT or UPDATE_STATUS
   * @param formValues vaccinelist form values
   * @param vfcItemSeq vfcItemSeq being updated
   */
  async manageVaccine(action, formValues, vfcItemSeq) {
    const request = {
      action,
      contractNumber: formValues.contractNumber,
      cdcCost: formValues.cdcCost,
      contractBeginDate: this.helperService.formatDate(formValues.contractBeginDate),
      contractEndDate: this.helperService.formatDate(formValues.contractEndDate),
      directShip: this.helperService.getCheckboxValue(formValues.directShipment),
      vaccineStatus: formValues.vaccineStatus,
      vfcItemSeq,
    };
    return await lastValueFrom(this.apiService.post(CONFIG.MANAGE_VACCINE, request));
  }

  /**
   * manage VFC Vaccine Brand mapping
   * @param facilityType vfc facility type
   * @param vfcType vfc type
   * @param vfcVaccineBrandList vfc vaccine brand list
   * @param orderWorkflow indicate if the workflow is VFC or COVID orders
   */
  async manageVFCVaccineMap(facilityType, vfcType, vfcVaccineBrandList, orderWorkflow = ORDER_WORKFLOW.VFC) {
    const request = {
      facilityType,
      vfcType,
      vfcVaccineBrandList,
      orderWorkflow,
    };

    return await lastValueFrom(this.apiService.post(CONFIG.MANAGE_VFC_VACCINE_MAP, request));
  }

  /**
   * check user access for facility type setup
   */
  async checkManageFacilityTypeSetup() {
    return this.compAccessService.checkAccess([COMPONENTS.VACCINE_ORDERING.FACILITY_TYPE_SETUP]);
  }

  /**
   * check if user has access to manage vaccine restrictions
   */
  async checkManageCovidVaccineList() {
    return this.compAccessService.checkAccess([COMPONENTS.COVID.COVID_VACCINE_RESTRICTIONS]);
  }
  /**
   * check user access for facility type setup
   */
  async checkManageCovidFacilityTypeSetup() {
    return this.compAccessService.checkAccess([COMPONENTS.COVID.COVID_FACILITY_TYPE_SETUP]);
  }

  /**
 * Retrieve VTRCKS Shipment List for the given provider
 * @param facilityId facilityId for whom the shipment list has to be retrieved
 */
  async getVTRCKSShipmentList(facilityId) {
    const request = {
      objects: [
        {
          name: OPERATION.INVENTORY.GET_FACILITY_VTRCKS_SHIPMENT,
          params: {
            facilityId,
          },
        },
      ],
    };

    let shipmentList = [];

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      shipmentList = result.objects[0].json;
    }

    return shipmentList;
  }

  /**
 * search user for the profile type
 * @param profileType profile type of user
 */
  async searchUserByProfile(profileType) {
    const request = {
      objects: [
        {
          name: OPERATION.USER.SEARCH_USER,
          params: {
            profileType,
          },
        },
      ],
    };

    let usersList = [];
    //CONFIG.GET_USER_BUNDLE
    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_USER_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json && result.objects[0].json.user) {
      usersList = result.objects[0].json.user.sort((a, b) => {
        if (a.status > b.status) {
          return 1;
        }
        if (a.status < b.status) {
          return -1;
        }
        if (a.lastName > b.lastName) {
          return 1;
        }
        if (a.lastName < b.lastName) {
          return -1;
        }
      });

      usersList = usersList.filter((x) => x.status === 'NEW' || x.status === 'ACTIVE');

      for (const user of usersList) {
        user.name = `${user.firstName} ${user.lastName}`;
      }
    }

    return usersList;
  }

  /**
  * Retrieves General Accordion information
  * @param facilityId Facility ID for whom information has to be retrieved
  * @param facilityIdSeq Seq Number of the Facility Id
  */
  async getFacilityInfo(facilityId, facilityIdSeq) {
    const request = {
      objects: [
        {
          name: OPERATION.FACILITY.GET_FACILITY_GENERAL_INFO,
          params: {
            facilityId,
            facilityIdSeq,
            facilityHrchy: CONSTANTS.FACILITY_HRCHY_AF,
          },
        },
      ],
    };

    const contactRequest = {
      objects: [
        {
          name: OPERATION.GLOBAL.GET_PHONE,
          params: {
            facilityId,
          },
        },
        {
          name: OPERATION.GLOBAL.GET_EMAIL,
          params: {
            facilityId,
          },
        },
      ],
    };

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_FACILITY_BUNDLE, request));

    const facilityInfoResult = {} as any;
    facilityInfoResult.facilityList = result.objects[0].json.facilityInfo.json;
    if (result.status === CONSTANTS.SUCCESS || result.status === CONSTANTS.TRN_WARNING) {
      facilityInfoResult.status = CONSTANTS.SUCCESS;
      if (result.objects && result.objects.length > 0 && result.objects[0].json) {
        if (result.objects[0].json && result.objects[0].json.length > 0) {
          facilityInfoResult.facilityList = result.objects[0];
        }
      }
    } else {
      facilityInfoResult.status = CONSTANTS.TRN_ERROR;
    }

    const contactResult = await lastValueFrom(this.apiService.post(CONFIG.GET_CORE_GLOBAL_BUNDLE, contactRequest));
    if (contactResult.status === CONSTANTS.SUCCESS || contactResult.status === CONSTANTS.TRN_WARNING) {
      facilityInfoResult.status = CONSTANTS.SUCCESS;
      const response = {} as any;
      if (contactResult.objects) {
        for (const resultObj of contactResult.objects) {
          if (resultObj.name) {
            response[resultObj.name] = resultObj.json;
          }
        }
      }
      if (response[OPERATION.GLOBAL.GET_PHONE]) {
        facilityInfoResult.phoneList = response[OPERATION.GLOBAL.GET_PHONE];
      }
      if (response[OPERATION.GLOBAL.GET_EMAIL]) {
        facilityInfoResult.emailList = response[OPERATION.GLOBAL.GET_EMAIL];
      }
    }
    else {
      facilityInfoResult.status = CONSTANTS.TRN_ERROR;
    }

    return facilityInfoResult;
  }


  /**
  * get menu key for tabs
  * @param mode mode of VFC
  */
  getMenuKey(mode) {
    let menu = '';
    switch (mode) {
      case VFC_MODES.VFC_ADMIN_EDIT: {
        menu = VFC_MENU.VFC_MENU_ADMIN_EDIT;
        break;
      }
      case VFC_MODES.VFC_ENROLL_ADMIN: {
        menu = VFC_MENU.VFC_MENU_ENROLL_ADMIN;
        break;
      }
      case VFC_MODES.VFC_RE_ENROLL: {
        menu = VFC_MENU.VFC_MENU_RE_ENROLL;
        break;
      }
      case VFC_MODES.VFC_ENROLL: {
        menu = VFC_MENU.VFC_MENU_ENROLL;
        break;
      }
    }

    return menu;
  }

  /**
 * Get Tabs for VFC
 * @param mode Mode of VFC
 */
  async getMenusForVFC(mode) {
    const request = {
      key: this.getMenuKey(mode),
    };

    const vfcMenus = {
      isError: false,
      data: [],
    };

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_CORE_LOVS, request));

    if (result.status === CONSTANTS.SUCCESS || result.status === CONSTANTS.TRN_WARNING) {
      vfcMenus.data = result.json;
    }

    return vfcMenus;
  }


  /**
 * Get Facility Audit Note for a Facility
 * @param facilityId facilityId for which audit note is to be retrieved
 */
  async getFacilityNote(facilityId) {
    const request = {
      objects: [
        {
          name: OPERATION.GLOBAL.GET_NOTE,
          params: {
            facilityId,
          },
        },
      ],
    };

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_CORE_GLOBAL_BUNDLE, request));

    const facilityNote = {
      isError: false,
      data: [],
    };

    if (this.helperService.isValidGetResult(result)) {
      facilityNote.data = this.parseFacilityNote(result.objects[0].json);
    } else {
      facilityNote.isError = true;
    }

    return facilityNote;
  }


  /**
  * Return noteList not having InterfaceProfileMapSeq and add VisibilityToList based on PROFILE_TYPE Lov,
  * also remove ADMIN PROFILE from VISIBILIT LIST
  * @param noteList audit Note List
  */
  parseFacilityNote(noteList) {
    const profileTypeSuperAdminVal = this.resourceManagerService.getLovColumnProperties(
      LOV_KEYS.PROFILE_TYPE,
      'Super Admin',
      'columnValue',
      'descr'
    );
    let index = 0;
    return noteList.filter((note) => {
      note.serialNum = index;
      note.comments = this.helperService.decodeJsonString(note.comments);
      note.origVisibilityToList = [];
      note.visibilityToList = [];
      note.visibilityToListDescr = '';
      note.profileList = note.noteBy = note.doneBy || note.userCreated;
      if (note.visibilityList) {
        for (const visibility of note.visibilityList) {
          if (visibility !== profileTypeSuperAdminVal) {
            const profile = this.resourceManagerService.getLovItem(LOV_KEYS.PROFILE_TYPE, visibility);
            if (profile) {
              note.visibilityToList.push(profile);
              note.origVisibilityToList.push(profile);
              if (!this.helperService.IsNullOrEmpty(note.visibilityToListDescr)) {
                note.visibilityToListDescr += `, ${profile.descr}`;
              } else {
                note.visibilityToListDescr = profile.descr;
              }
            }
          }
        }
      }
      index++;
      return this.helperService.IsNullOrEmpty(note.intfcProfileMapSeq);
    });
  }




  /**
    * Saves the Agreement tab
    * @param mode mode of the VFC Wizard
    * @param covidVaccineEnrollmentData COVID Vaccine Enrollment data
    * @param formData formData from the Agreement tab
    */

  /**
    * Saves the Agreement tab
    * @param mode mode of the VFC Wizard
    * @param covidVaccineEnrollmentData COVID Vaccine Enrollment data
    * @param formData formData from the Agreement tab
    */

  async saveAgreement(mode, covidVaccineEnrollmentData, formData) {
    const request = {
      action: "ADD",
      mode,
      vfcPin: covidVaccineEnrollmentData.vfcPin,
      vfcIdSeq: '',
      facilityId: covidVaccineEnrollmentData.facilityId,
      facilityIdSeq: covidVaccineEnrollmentData.facilityIdSeq,
      lastCreatedTab: formData.lastCreatedTab,
      lastUpdatedTab: formData.lastUpdatedTab,
      vfcEnrollFormSeq: covidVaccineEnrollmentData.vfcEnrollFormSeq,
      fileName: formData.data.value.fileName,
      fileSize: formData.data.value.fileSize,
      fileType: formData.data.value.fileType,
      fileRequestType: "VFC_AGREEMENT",
      VFCRequestedTab: STATE_FUND_ENROLL_COLUMN_VALUES.AGREEMENT,
      encodedData: formData.data.value.encodedData,
      fileSeq: "",
      vfcStatus: "IN PROGRESS",
      agreement: {
        cmoSignDate: formData.data.value.cmoSignDate,
      }
    };

    return await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_FILE_UPLOAD, request));
  }


  async downloadAgreementFileData(request){
    const result = await lastValueFrom(this.apiService.msPost(CONFIG.DOWNLOAD_FILE, request));
    return result;
  }


}
